import React from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Divider, Typography } from 'antd';
import { prepareLinkToFleethandTaskList } from 'shared/utils/fleethand';
import { formatWithPostfixOrEmpty } from 'shared/utils/format-with-postfix-or-empty';
import { formatDate } from 'shared/utils/time/format-date';
import { formatDateTime } from 'shared/utils/time/format-date-time.ts';
import { CURRENCY_SIGN, ECurrency } from 'types/currencies';
import { TransportSubTypes, TransportType } from 'types/transport';
import { CardUi } from 'components/common/card-ui';
import { TransportAvailabilityStatus } from 'components/transport/transport-availability-status';
import { VehicleAlert } from 'components/transport/vehicle-alert';
import { TripRoute } from 'components/trips/trip-route';
import styles from './trip-main-info.module.scss';
import type { ITripMainInfoProps } from './types';

export const TripMainInfo: React.FC<ITripMainInfoProps> = (props) => {
  const { trip } = props;

  const { t } = useTranslation();

  const { trailerPlate, trailer, truck, status } = trip;
  const displayTrailer = (trailer && `${trailer.plateNumber} - ${TransportSubTypes[trailer.subType]}`) || trailerPlate;
  const isNonCompanyTrailer = Boolean(trailerPlate && !trailer);
  const isNotDone = status !== 'done';

  const renderCostPrice = () => {
    const isDefaultCurrency = trip.currency === ECurrency.EUR;

    if (!trip.costPrice) {
      return (
        <Flex align='center' gap='small'>
          <strong>Cost price:</strong>
          {formatWithPostfixOrEmpty(trip.calculatedCostPrice, CURRENCY_SIGN[ECurrency.EUR])}{' '}
          <Typography.Text type='secondary'>(Calculated)</Typography.Text>
        </Flex>
      );
    }

    return (
      <Flex align='center' gap='small'>
        <strong>Cost price:</strong>
        {isDefaultCurrency ? (
          <span>{formatWithPostfixOrEmpty(trip.costPrice, CURRENCY_SIGN[trip.currency])} </span>
        ) : (
          <>
            <span>≈ {formatWithPostfixOrEmpty(trip.costPriceInDefault, CURRENCY_SIGN[ECurrency.EUR])} </span>
            <Typography.Text type='secondary'>
              {formatWithPostfixOrEmpty(trip.costPrice, CURRENCY_SIGN[trip.currency])}
            </Typography.Text>
          </>
        )}
        <Typography.Text type='secondary'>(Manual)</Typography.Text>
      </Flex>
    );
  };

  return (
    <>
      <CardUi>
        <Flex gap='small' vertical>
          <Flex gap='small'>
            <TripRoute warehousesIds={trip.relatedWarehouseIds} />
          </Flex>
          <Flex gap='small'>
            <strong>{t('trip:estimated_start_date')}: </strong>
            {formatDate(trip.estimatedStartDate)}
          </Flex>

          <Flex gap='small'>
            <strong>{t('trip:estimated_end_date')}: </strong>
            {formatDate(trip.estimatedEndDate)}
          </Flex>

          {trip.startDate && (
            <Flex gap='small'>
              <strong>{t('trip:start_date')}: </strong>
              {formatDateTime(trip.startDate)}
            </Flex>
          )}
          {trip.endDate && (
            <Flex gap='small'>
              <strong>{t('trip:end_date')}: </strong>
              {formatDateTime(trip.endDate)}
            </Flex>
          )}

          <Divider className={styles.divider} />

          <Flex align='center' gap='small'>
            <strong>Freight sum:</strong>
            {formatWithPostfixOrEmpty(trip.totalFreight, '€')}
          </Flex>
          {renderCostPrice()}

          <Flex align='center' gap='small'>
            <strong>Cost price by map:</strong>
            {formatWithPostfixOrEmpty(trip.costPriceByMap, '€')}
          </Flex>

          <Flex align='center' gap='small'>
            <strong>Useless km cost:</strong>
            {formatWithPostfixOrEmpty(trip.uselessKmCost, '€')}
          </Flex>

          <Flex align='center' gap='small'>
            <strong>Mileage by map:</strong>
            {formatWithPostfixOrEmpty(trip.mileageByMap, 'km')}
          </Flex>

          <Divider className={styles.divider} />

          <Flex align='center' gap='small'>
            <strong>Manager:</strong>
            {trip.user.name}
          </Flex>

          <Flex align='center' gap='small'>
            <strong>{t('common:created')}: </strong> {formatDateTime(trip.createdAt)}
          </Flex>
        </Flex>
      </CardUi>

      {!trip.soldToPartners && (
        <CardUi>
          <Flex gap='small' vertical>
            <Flex gap='small' vertical>
              {truck ? (
                <Flex align='center' gap='small'>
                  <strong>Truck:</strong>
                  {truck.plateNumber} - {TransportSubTypes[truck.subType]}
                </Flex>
              ) : null}
              {displayTrailer && (
                <Flex align='center' gap='small'>
                  <strong>Trailer:</strong>
                  <span className={isNonCompanyTrailer ? styles.nonCompanyTrailer : ''}>{displayTrailer}</span>
                </Flex>
              )}
            </Flex>
            <Flex gap='large'>
              <Flex gap='small'>
                <strong>Max LDM: </strong>
                {trip?.trailer?.maxLDM || trip.truck?.maxLDM || '-'}m
              </Flex>
              <Flex gap='small'>
                <strong>Max Weight: </strong> {trip?.trailer?.maxWeight || trip.truck?.maxWeight || '-'}kg
              </Flex>
            </Flex>

            {trip.truck?.fleethandVehicleId && (
              <Flex gap='large'>
                <a
                  href={prepareLinkToFleethandTaskList(trip.truck?.fleethandVehicleId)}
                  target='_blank'
                  rel='noreferrer noopener'>
                  Fleethand tasks list
                </a>
              </Flex>
            )}

            <TransportAvailabilityStatus
              status={trip.status}
              transportType={TransportType.truck}
              transportId={trip.truckId}
            />
            <TransportAvailabilityStatus
              status={trip.status}
              transportType={TransportType.trailer}
              transportId={trip.trailerId}
            />
            <Flex gap='small' vertical>
              {truck && isNotDone && (
                <VehicleAlert
                  inService={truck.inService}
                  needService={truck.needService}
                  serviceComment={truck.serviceComment}
                  plateNumber={truck.plateNumber}
                />
              )}

              {trailer && isNotDone && (
                <VehicleAlert
                  inService={trailer.inService}
                  needService={trailer.needService}
                  serviceComment={trailer.serviceComment}
                  plateNumber={trailer.plateNumber}
                />
              )}
            </Flex>
          </Flex>
        </CardUi>
      )}
    </>
  );
};
